import {DOCUMENT} from '@angular/common';
import {Inject, Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {GERMAN_UI_TRANSLATIONS} from '../i18n/ui-translations';

export type AppLanguage = 'en' | 'de';

@Injectable({providedIn: 'root'})
export class LanguageService {
  private readonly storageKey = 'mals-language';
  private readonly languageSubject: BehaviorSubject<AppLanguage>;
  readonly language$;

  constructor(@Inject(DOCUMENT) private readonly document: Document) {
    const stored = localStorage.getItem(this.storageKey);
    const initial: AppLanguage = stored === 'de' ? 'de' : 'en';
    this.languageSubject = new BehaviorSubject<AppLanguage>(initial);
    this.language$ = this.languageSubject.asObservable();
    this.document.documentElement.lang = initial;
  }

  get currentLanguage(): AppLanguage {
    return this.languageSubject.value;
  }

  setLanguage(language: AppLanguage): void {
    if (language === this.currentLanguage) {
      return;
    }
    localStorage.setItem(this.storageKey, language);
    this.document.documentElement.lang = language;
    this.languageSubject.next(language);
  }

  translate(key: string | null | undefined, params: Record<string, string | number> = {}): string {
    if (!key) {
      return '';
    }
    const translated = this.currentLanguage === 'de' ? (GERMAN_UI_TRANSLATIONS[key] ?? key) : key;
    return Object.entries(params).reduce(
      (text, [name, value]) => text.split(`{${name}}`).join(String(value)),
      translated
    );
  }

  localized(english?: string, german?: string): string {
    const safeEnglish = english ?? '';
    const safeGerman = german ?? '';
    return this.currentLanguage === 'de' ? (safeGerman || safeEnglish) : (safeEnglish || safeGerman);
  }
}
